import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { map, Observable, shareReplay, tap } from 'rxjs';

import { environment } from '../../environments/environment';

interface CpdepPhaseDto {
  id: number;
  name: string;
}

interface DesignProgressDto {
  id: number;
  name: string;
}

interface EstimateRequestTypeDto {
  id: number;
  name: string;
}

interface IbpYearDto {
  id: number;
  name: string;
}

interface ProjectManagementTypeDto {
  id: number;
  name: string;
}

interface ProjectTeamDto {
  id: number;
  name: string;
}

interface TurnaroundYearDto {
  id: number;
  name: string;
}

interface UnitDto {
  id: number;
  name: string;
}

interface AreaDto {
  id: number;
  name: string;
}

interface EquipmentDto {
  id: number;
  name: string;
}

interface ContingencyCalculationMethodDto {
  id: number;
  name: string;
}

interface TypeDto {
  id: number;
  name: string;
}

interface AlternativeNumberDto {
  id: number;
  name: string;
}

interface RevisionDto {
  id: number;
  name: string;
}

export interface DictionaryItemDto {
  id: number;
  name: string;
}

@Injectable({ providedIn: 'root' })
export class DictionariesApiService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = environment.apiBaseUrl;
  private readonly dictionaryItemsCache = new Map<string, Observable<DictionaryItemDto[]>>();

  getCpdepPhases(): Observable<string[]> {
    return this.http
      .get<CpdepPhaseDto[]>(`${this.apiBaseUrl}/api/CpdepPhases`)
      .pipe(map(rows => this.normalizeValues(this.sortById(rows).map(row => row.name))));
  }

  getDesignProgresses(): Observable<string[]> {
    return this.http
      .get<DesignProgressDto[]>(`${this.apiBaseUrl}/api/DesignProgresses`)
      .pipe(map(rows => this.normalizeValues(this.sortById(rows).map(row => row.name))));
  }

  getEstimateRequestTypes(): Observable<string[]> {
    return this.http
      .get<EstimateRequestTypeDto[]>(`${this.apiBaseUrl}/api/EstimateRequestTypes`)
      .pipe(map(rows => this.normalizeValues(this.sortById(rows).map(row => row.name))));
  }

  getIbpYears(): Observable<string[]> {
    return this.http
      .get<IbpYearDto[]>(`${this.apiBaseUrl}/api/IbpYears`)
      .pipe(map(rows => this.normalizeValues(this.sortById(rows).map(row => row.name))));
  }

  getProjectManagementTypes(): Observable<string[]> {
    return this.http
      .get<ProjectManagementTypeDto[]>(`${this.apiBaseUrl}/api/ProjectManagementTypes`)
      .pipe(
        map(rows => this.sortById(rows)),
        map(rows => this.normalizeValues(rows.map(row => row.name))),
      );
  }

  getProjectTeams(): Observable<string[]> {
    return this.http
      .get<ProjectTeamDto[]>(`${this.apiBaseUrl}/api/ProjectTeams`)
      .pipe(map(rows => this.normalizeValues(this.sortById(rows).map(row => row.name))));
  }

  getTurnaroundYears(): Observable<string[]> {
    return this.http
      .get<TurnaroundYearDto[]>(`${this.apiBaseUrl}/api/TurnaroundYears`)
      .pipe(map(rows => this.normalizeValues(this.sortById(rows).map(row => row.name))));
  }

  getUnits(): Observable<string[]> {
    return this.http
      .get<UnitDto[]>(`${this.apiBaseUrl}/api/Units`)
      .pipe(map(rows => this.normalizeValues(this.sortById(rows).map(row => row.name))));
  }

  getAreas(): Observable<string[]> {
    return this.http
      .get<AreaDto[]>(`${this.apiBaseUrl}/api/Areas`)
      .pipe(map(rows => this.normalizeValues(this.sortById(rows).map(row => row.name))));
  }

  getEquipments(): Observable<string[]> {
    return this.http
      .get<EquipmentDto[]>(`${this.apiBaseUrl}/api/Equipments`)
      .pipe(map(rows => this.normalizeValues(this.sortById(rows).map(row => row.name))));
  }

  getContingencyCalculationMethods(): Observable<string[]> {
    return this.http
      .get<ContingencyCalculationMethodDto[]>(`${this.apiBaseUrl}/api/ContingencyCalculationMethods`)
      .pipe(map(rows => this.normalizeValues(this.sortById(rows).map(row => row.name))));
  }

  getTypes(): Observable<string[]> {
    return this.http
      .get<TypeDto[]>(`${this.apiBaseUrl}/api/Types`)
      .pipe(map(rows => this.normalizeValues(this.sortById(rows).map(row => row.name))));
  }

  getAlternativeNumbers(): Observable<string[]> {
    return this.http
      .get<AlternativeNumberDto[]>(`${this.apiBaseUrl}/api/AlternativeNumbers`)
      .pipe(map(rows => this.normalizeValues(this.sortById(rows).map(row => row.name))));
  }

  getRevisions(): Observable<string[]> {
    return this.http
      .get<RevisionDto[]>(`${this.apiBaseUrl}/api/Revisions`)
      .pipe(map(rows => this.normalizeValues(this.sortById(rows).map(row => row.name))));
  }

  getDictionaryItems(resource: string): Observable<DictionaryItemDto[]> {
    return this.http
      .get<DictionaryItemDto[]>(`${this.apiBaseUrl}/api/${resource}`)
      .pipe(map(items => this.normalizeItems(items)));
  }

  getDictionaryItemsCached(resource: string): Observable<DictionaryItemDto[]> {
    const cached = this.dictionaryItemsCache.get(resource);
    if (cached) {
      return cached;
    }

    const request$ = this.getDictionaryItems(resource).pipe(shareReplay(1));
    this.dictionaryItemsCache.set(resource, request$);
    return request$;
  }

  findDictionaryId(items: readonly DictionaryItemDto[], value: string): number {
    const normalized = this.normalizeDictionaryMatchValue(value);
    if (!normalized) {
      return 0;
    }

    const exactMatch = items.find(item => this.normalizeDictionaryMatchValue(item.name) === normalized);
    if (exactMatch) {
      return exactMatch.id;
    }

    const comparable = this.toComparableDictionaryKey(normalized);
    const fuzzyMatch = items.find(
      item => this.toComparableDictionaryKey(this.normalizeDictionaryMatchValue(item.name)) === comparable,
    );
    return fuzzyMatch?.id ?? 0;
  }

  createDictionaryItem(resource: string, name: string): Observable<number> {
    return this.http
      .post<number>(`${this.apiBaseUrl}/api/${resource}`, { name })
      .pipe(tap(() => this.invalidateDictionaryCache(resource)));
  }

  updateDictionaryItem(resource: string, id: number, name: string): Observable<void> {
    return this.http
      .put<void>(`${this.apiBaseUrl}/api/${resource}/${id}`, { name })
      .pipe(tap(() => this.invalidateDictionaryCache(resource)));
  }

  deleteDictionaryItem(resource: string, id: number): Observable<void> {
    return this.http
      .delete<void>(`${this.apiBaseUrl}/api/${resource}/${id}`)
      .pipe(tap(() => this.invalidateDictionaryCache(resource)));
  }

  private normalizeItems(values: readonly DictionaryItemDto[]): DictionaryItemDto[] {
    const sortedValues = this.sortById(values);
    const unique = new Map<string, DictionaryItemDto>();
    for (const value of sortedValues) {
      const normalized = value.name?.trim();
      if (!normalized) {
        continue;
      }

      const key = normalized.toLocaleLowerCase();
      if (!unique.has(key)) {
        unique.set(key, { id: value.id, name: normalized });
      }
    }
    return [...unique.values()];
  }

  private sortById<T extends { id: number }>(values: readonly T[]): T[] {
    return [...values].sort((a, b) => a.id - b.id);
  }

  private normalizeValues(values: readonly string[]): string[] {
    const unique = new Set<string>();
    for (const value of values) {
      const normalized = value.trim();
      if (normalized) {
        unique.add(normalized);
      }
    }
    return [...unique];
  }

  private normalizeDictionaryMatchValue(value: string): string {
    return value
      .toLowerCase()
      .normalize('NFKC')
      .replace(/[–—−]/g, '-')
      .replace(/\s*\/\s*/g, '/')
      .replace(/\s*-\s*/g, '-')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private toComparableDictionaryKey(value: string): string {
    return value.replace(/[^a-z0-9]+/g, '');
  }

  private invalidateDictionaryCache(resource: string): void {
    this.dictionaryItemsCache.delete(resource);
  }
}